import { Component } from '@angular/core';

export interface FaqItem {
  question: string;
  /** Respuesta directa de 2-3 frases, citable por buscadores y asistentes de IA. Admite HTML inline (enlaces). */
  answer: string;
}

/**
 * Preguntas frecuentes de la portada.
 *
 * Las preguntas y respuestas deben mantenerse sincronizadas con el bloque
 * FAQPage del JSON-LD de src/index.html y con src/seo/index.md.
 */
@Component({
  selector: 'app-faq',
  templateUrl: './faq.component.html',
  styleUrls: ['./faq.component.css']
})
export class FaqComponent {

  /** Fecha de la última revisión del contenido (E-E-A-T). Formato ISO para <time datetime>. */
  readonly updatedIso = '2026-10-14';
  readonly updatedLabel = '14 de octubre de 2026';

  faqs: FaqItem[] = [
    {
      question: '¿Cómo funciona una sesión de terapia en el Consultorio Bien-Estar?',
      answer:
        'Puedes elegir una sesión virtual, por videollamada, o una sesión presencial en el consultorio de Bogotá; ' +
        'en ambos casos puede ser individual (1 a 1) o de pareja y dura alrededor de una hora. ' +
        'Para la sesión virtual eliges profesional, día y hora en el calendario de disponibilidad de cada ' +
        '<a href="#proyectos">servicio</a> y recibes el enlace de conexión al confirmar la reserva; ' +
        'solo necesitas un dispositivo con cámara, conexión a internet y un espacio privado.'
    },
    {
      question: '¿Cuánto cuesta una sesión y cómo se paga?',
      answer:
        'El precio de cada sesión y de cada producto digital se publica en pesos colombianos (COP) en su ficha, ' +
        'dentro de la sección <a href="#proyectos">Servicios</a>. El pago se hace en línea con tarjeta de crédito ' +
        'o débito, PSE o Nequi a través de la pasarela Wompi, y si vives fuera de Colombia puedes pagar con tarjeta internacional.'
    },
    {
      question: '¿Atienden a personas que viven fuera de Colombia?',
      answer:
        'Sí. El consultorio está en Bogotá, donde también atendemos de forma presencial, y en modalidad virtual ' +
        'acompañamos en español a personas de Latinoamérica, España y el resto del mundo. Al agendar, el horario se ' +
        'muestra en hora de Bogotá (UTC-5); solo tienes que ajustarlo a tu zona horaria.'
    },
    {
      question: '¿Qué problemas tratan los psicólogos del Consultorio Bien-Estar?',
      answer:
        'El consultorio lo dirige Natalia Güechá Nieto, magíster en psicología clínica y especialista en intervención ' +
        'psicológica en situaciones de crisis, junto a un equipo de psicólogos clínicos. Las áreas de trabajo más habituales ' +
        'son ansiedad y estrés, ataques de pánico, autoestima, terapia de pareja, duelo, regulación emocional y crecimiento ' +
        'personal. Puedes conocer al <a href="#equipo">equipo de psicólogos</a> en la sección Quiénes somos.'
    },
    {
      question: '¿Cómo agendo mi primera consulta?',
      answer:
        'Para una sesión virtual, entra en <a href="#proyectos">Servicios</a>, abre la ficha de la sesión que te interesa, ' +
        'elige profesional, fecha y hora, y completa el pago en línea; la reserva queda confirmada al instante. ' +
        'Para una sesión presencial en Bogotá, o si prefieres resolver dudas antes, escribe por WhatsApp al ' +
        '<a href="https://wa.me/573104671284" target="_blank" rel="noopener">+57 310 4671284</a> o ' +
        'usa el formulario de <a href="#contacto">contacto</a>.'
    },
    {
      question: '¿La terapia es confidencial? ¿Y si estoy en crisis?',
      answer:
        'Sí: la información de las sesiones, virtuales o presenciales, está protegida por el secreto profesional ' +
        '(Ley 1090 de 2006) y por la <a href="/privacy">política de tratamiento de datos</a> del consultorio. ' +
        'Este sitio no atiende urgencias: si estás en riesgo vital, llama a la línea de emergencias de tu país ' +
        '(123 en Colombia, 112 en España) o acude a urgencias.'
    }
  ];

  /** Índices de las preguntas desplegadas. Cada una se abre/cierra de forma independiente. */
  private readonly openItems = new Set<number>();

  isOpen(index: number): boolean {
    return this.openItems.has(index);
  }

  toggle(index: number): void {
    if (this.openItems.has(index)) {
      this.openItems.delete(index);
    } else {
      this.openItems.add(index);
    }
  }

  trackByQuestion(_index: number, item: FaqItem): string {
    return item.question;
  }
}
